import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

const SWIPER_FOCUS_TAB_AFTER = '_focusActiveTabAfterTransition';

export interface SliderInterface {
  initial: string;
  currentIndex: number;
  slidesPerView: number;
  spaceBetween: number;
  autoplay: boolean;
  direction: 'horizontal' | 'vertical';
  paginationType: 'progressbar' | 'bullets' | 'fraction' | 'custom';
  paused: boolean;
  init(): void;
  getTotalSlide(): number;
  isActiveSlide(currentIndex: number): boolean;
  destroySlider(): void;
  toggleVideo(): void;
  defaultSwiperSettings: {};
  setDefaultSettings(): void;
  setAutoPlay(): void;
  setCustomPagination(): void;
  getPaginationContainer(swiper: Swiper): HTMLElement | null;
  bindPaginationTabClicks(swiper: Swiper): void;
  focusActiveTabIfRequested(swiper: Swiper): void;
  updateAriaSelectedOnTabs(swiper: Swiper): void;
  swiper: Swiper | null;
}
const Slider = ({
  initial,
  slidesPerView,
  spaceBetween,
  autoplay,
  direction,
  paginationType,
}: {
  initial: string;
  slidesPerView: number;
  spaceBetween: number;
  autoplay: boolean;
  direction: 'horizontal' | 'vertical';
  paginationType: 'progressbar' | 'bullets' | 'fraction' | 'custom';
}): SliderInterface => ({
  initial: initial ? initial : '.swiper',
  slidesPerView: slidesPerView ? slidesPerView : 1,
  currentIndex: 1,
  spaceBetween: spaceBetween ? spaceBetween : 0,
  direction: direction ? direction : 'horizontal',
  autoplay: autoplay ? autoplay : false,
  paginationType: paginationType ? paginationType : 'bullets',
  swiper: null,
  defaultSwiperSettings: {},
  paused: false,
  init(): void {
    this.setDefaultSettings();
    this.setAutoPlay();

    if (this.paginationType == 'custom') this.setCustomPagination();

    this.swiper = new Swiper(this.initial, this.defaultSwiperSettings);
  },
  setAutoPlay(): void {
    if (this.autoplay) {
      this.defaultSwiperSettings = {
        ...this.defaultSwiperSettings,
        ...{
          autoplay: {
            delay: 2500,
            disableOnInteraction: true,
          },
        },
      };
    }
  },
  setDefaultSettings(): void {
    this.defaultSwiperSettings = {
      slidesPerView: 1,
      spaceBetween: this.spaceBetween,
      modules: [Navigation, Pagination, Autoplay],
      direction: this.direction,
      navigation: {
        nextEl: this.initial + '-swiper-btn-next',
        prevEl: this.initial + '-swiper-btn-prev',
      },
      autoHeight: false,
      pagination: {
        el: this.initial + '-swiper-pagination',
        type: this.paginationType,
      },
      breakpoints: {
        768: {
          slidesPerView: this.slidesPerView,
        },
      },
      on: {
        transitionEnd: () => {
          this.currentIndex = this.swiper!.realIndex + 1;
        },
      },
    };
  },
  setCustomPagination(): void {
    const existingOn =
      (this.defaultSwiperSettings as { on?: Record<string, unknown> }).on ||
      {};
    this.defaultSwiperSettings = {
      ...this.defaultSwiperSettings,
      ...{
        autoHeight: true,
        pagination: {
          el: this.initial + '-swiper-pagination',
          type: this.paginationType,
          renderCustom: function (
            swiper: Swiper,
            _current: number,
            total: number,
          ) {
            const current = swiper.realIndex;
            let paginationCustom: string = '';
            for (let i = 0; i < total; i++) {
              const ariaLabel =
                swiper.slides[i].firstElementChild?.getAttribute('data-slide') ||
                `Slide ${i + 1}`;
              const isSelected = i === current;
              const activeClasses = isSelected
                ? ' swiper-pagination-bullet-active !opacity-100'
                : ' opacity-25';
              paginationCustom +=
                '<button type="button" role="tab" aria-selected="' +
                (isSelected ? 'true' : 'false') +
                '" aria-label="' +
                ariaLabel +
                '" class="!bg-white !rounded-none !relative w-5 w-[4px] h-[19px] lg:h-[34px] transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white focus-visible:!opacity-100 swiper-pagination-bullet' +
                activeClasses +
                '"></button>';
            }
            return paginationCustom;
          },
        },
        on: {
          ...existingOn,
          init: (swiper: Swiper) => {
            this.bindPaginationTabClicks(swiper);
          },
          slideChange: (swiper: Swiper) => {
            this.updateAriaSelectedOnTabs(swiper);
          },
          slideChangeTransitionEnd: (swiper: Swiper) => {
            this.updateAriaSelectedOnTabs(swiper);
            this.focusActiveTabIfRequested(swiper);
          },
        },
      },
    };
  },
  getTotalSlide(): number {
    return this.swiper!.slides.length;
  },
  destroySlider(): void {
    if (this.swiper != undefined) this.swiper.destroy();
  },
  isActiveSlide(currentIndex: number): boolean {
    return currentIndex == this.swiper!.realIndex ? true : false;
  },
  toggleVideo(): void {
    this.paused ? this.swiper!.autoplay.start() : this.swiper!.autoplay.stop();
    this.paused = !this.paused;
  },
  getPaginationContainer(swiper: Swiper): HTMLElement | null {
    const paginationEl = swiper.pagination?.el;
    if (!paginationEl) return null;
    return typeof paginationEl === 'string'
      ? document.querySelector<HTMLElement>(paginationEl)
      : (paginationEl as HTMLElement);
  },
  bindPaginationTabClicks(swiper: Swiper): void {
    const container = this.getPaginationContainer(swiper);
    if (!container) return;
    container.addEventListener('click', (e: Event) => {
      const target = (e.target as HTMLElement).closest('[role="tab"]');
      if (!target) return;
      const tabs = container.querySelectorAll<HTMLElement>('[role="tab"]');
      const index = Array.from(tabs).indexOf(target as HTMLElement);
      if (index >= 0) {
        (swiper as Swiper & Record<string, unknown>)[SWIPER_FOCUS_TAB_AFTER] =
          true;
        swiper.slideTo(index);
      }
    });
  },
  focusActiveTabIfRequested(swiper: Swiper): void {
    const flag = (swiper as Swiper & Record<string, unknown>)[
      SWIPER_FOCUS_TAB_AFTER
    ];
    if (!flag) return;
    (swiper as Swiper & Record<string, unknown>)[SWIPER_FOCUS_TAB_AFTER] =
      false;
    const container = this.getPaginationContainer(swiper);
    if (!container) return;
    const tabs = container.querySelectorAll<HTMLElement>('[role="tab"]');
    const active = tabs[swiper.realIndex];
    if (active) active.focus();
  },
  updateAriaSelectedOnTabs(swiper: Swiper): void {
    const container = this.getPaginationContainer(swiper);
    if (!container) return;
    const tabs = container.querySelectorAll<HTMLElement>('[role="tab"]');
    const activeIndex = swiper.realIndex;
    tabs.forEach((tab, index) => {
      const isSelected = index === activeIndex;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.classList.toggle('swiper-pagination-bullet-active', isSelected);
      tab.classList.toggle('!opacity-100', isSelected);
      tab.classList.toggle('opacity-25', !isSelected);
    });
  },
});
export default Slider;